import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';
import { PushPin as PushPinIcon } from '@phosphor-icons/react/dist/ssr/PushPin';

import paths from '@src/paths';
import { isNavItemActive } from '@src/corelib';
import { usePathname, useSettings } from '@src/hooks';
import { RouterLink } from '@src/components/core/link';
import { Logo } from '@src/components/core/logo';
import { useAuth } from '@src/context';

import icons from '../icons';
import navColorStyles from './styles';
import WorkspacesSwitch from '../WorkspacesSwitch';

const logoColors = {
  dark: { blend_in: 'light', discrete: 'light', evident: 'light' },
  light: { blend_in: 'dark', discrete: 'dark', evident: 'light' },
};

const PIN_STORAGE_KEY = 'lg.sidebar.pinnedKeys';

function readPinnedKeys() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(PIN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function writePinnedKeys(keys) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // ignore
  }
}

const PinContext = React.createContext({
  isPinningEnabled: false,
  pinnedKeys: [],
  togglePin: () => {},
});

function flattenPinnableItems(items, acc = new Map()) {
  for (const item of items || []) {
    if (item?.items?.length) {
      flattenPinnableItems(item.items, acc);
    } else if (item?.href && item?.key) {
      acc.set(item.key, item);
    }
  }
  return acc;
}

function Sidebar({ color = 'evident', items = [] }) {
  const pathname = usePathname();
  const { state } = useAuth();
  const memberships = React.useMemo(() => {
    const user = state?.user;
    const userMemberships = user?.client_memberships || [];
    const isClientMember = !user?.is_staff && !user?.is_superuser && userMemberships.length > 0;
    return isClientMember ? userMemberships : [];
  }, [state?.user]);
  const primaryMembership = memberships[0] || null;
  const homeHref = React.useMemo(() => {
    const normalizeSlug = (value = '') =>
      value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const getMembershipDashboardSlug = (membership = {}) => {
      const clientSlug = membership?.client_slug || membership?.client_id;
      if (clientSlug) {
        return clientSlug;
      }
      const parentName = membership?.parent_company_name;
      if (parentName) {
        return normalizeSlug(parentName);
      }
      return membership?.parent_company_id || null;
    };
    if (!state?.user) {
      return paths.home;
    }
    if (primaryMembership) {
      const slug = getMembershipDashboardSlug(primaryMembership);
      return slug ? paths.clientDashboard(slug) : paths.clients;
    }
    return paths.dashboard.dailyPulse;
  }, [primaryMembership, state?.user]);

  const {
    settings: { colorScheme = 'light' },
  } = useSettings();

  const styles = navColorStyles[colorScheme][color];
  const logoColor = logoColors[colorScheme][color];

  const isPinningEnabled = Boolean(state?.user?.is_staff || state?.user?.is_superuser);
  const [pinnedKeys, setPinnedKeys] = React.useState(() => readPinnedKeys());

  const togglePin = React.useCallback((key) => {
    if (!key) return;
    setPinnedKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      writePinnedKeys(next);
      return next;
    });
  }, []);

  const pinnableLookup = React.useMemo(() => flattenPinnableItems(items), [items]);

  const itemsWithPinned = React.useMemo(() => {
    if (!isPinningEnabled || pinnedKeys.length === 0) return items;
    const pinnedItems = pinnedKeys
      .map((key) => pinnableLookup.get(key))
      .filter(Boolean);
    if (pinnedItems.length === 0) return items;
    return [
      { key: '__pinned__', title: 'Pinned', items: pinnedItems },
      ...items,
    ];
  }, [isPinningEnabled, items, pinnableLookup, pinnedKeys]);

  const pinContextValue = React.useMemo(
    () => ({ isPinningEnabled, pinnedKeys, togglePin }),
    [isPinningEnabled, pinnedKeys, togglePin],
  );

  return (
    <PinContext.Provider value={pinContextValue}>
      <Box
        sx={{
          ...styles,
          bgcolor: 'var(--SideNav-background)',
          borderRight: 'var(--SideNav-border)',
          color: 'var(--SideNav-color)',
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          height: '100%',
          left: 0,
          position: 'fixed',
          top: 0,
          width: 'var(--SideNav-width)',
          zIndex: 'var(--SideNav-zIndex)',
        }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <div>
            <Box component={RouterLink} href={homeHref} sx={{ display: 'inline-flex' }}>
              <Logo color={logoColor} />
            </Box>
          </div>
          {/*<WorkspacesSwitch />*/}
        </Stack>
        <Box
          component="nav"
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            p: 2,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {renderNavGroups({ items: itemsWithPinned, pathname })}
        </Box>
      </Box>
    </PinContext.Provider>
  );
}

const GROUP_STORAGE_KEY = 'lg.sidebar.openGroups';

function readGroupOverrides() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(GROUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeGroupOverrides(map) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function groupContainsActiveItem(items, pathname) {
  return (items || []).some((item) => {
    if (item.href && pathname.startsWith(item.href)) return true;
    if (item.items) return groupContainsActiveItem(item.items, pathname);
    return false;
  });
}

function NavGroup({ groupKey, title, items, pathname }) {
  const [overrides, setOverrides] = React.useState(() => readGroupOverrides());
  const hasActive = React.useMemo(() => groupContainsActiveItem(items, pathname), [items, pathname]);
  const userOverride = overrides[groupKey];
  const isOpen = title ? (userOverride !== undefined ? userOverride : hasActive) : true;
  const ExpandIcon = isOpen ? CaretDownIcon : CaretRightIcon;

  const toggle = React.useCallback(() => {
    setOverrides((prev) => {
      const currentlyOpen = prev[groupKey] !== undefined ? prev[groupKey] : hasActive;
      const next = { ...prev, [groupKey]: !currentlyOpen };
      writeGroupOverrides(next);
      return next;
    });
  }, [groupKey, hasActive]);

  return (
    <Stack component="li" spacing={1.5}>
      {title ? (
        <Box
          onClick={toggle}
          onKeyUp={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggle();
            }
          }}
          role="button"
          tabIndex={0}
          sx={{
            alignItems: 'center',
            borderRadius: 1,
            cursor: 'pointer',
            display: 'flex',
            gap: 1,
            px: '8px',
            py: '2px',
            userSelect: 'none',
            '&:hover': { bgcolor: 'var(--NavItem-hover-background)' },
          }}
        >
          <Typography
            sx={{ color: 'var(--NavGroup-title-color)', flex: '1 1 auto', fontSize: '0.875rem', fontWeight: 500 }}
          >
            {title}
          </Typography>
          <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
        </Box>
      ) : null}
      {isOpen ? <div>{renderNavItems({ depth: 0, items, pathname })}</div> : null}
    </Stack>
  );
}

function renderNavGroups({ items, pathname }) {
  return (
    <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map((group) => (
        <NavGroup
          groupKey={group.key}
          items={group.items}
          key={group.key}
          pathname={pathname}
          title={group.title}
        />
      ))}
    </Stack>
  );
}

function renderNavItems({ depth = 0, items = [], pathname }) {
  const children = items.reduce((acc, curr) => {
    const { items: childItems, key, ...item } = curr;

    const forceOpen = childItems
      ? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
      : false;

    acc.push(
      <NavItem depth={depth} forceOpen={forceOpen} itemKey={key} key={key} pathname={pathname} {...item}>
        {childItems ? renderNavItems({ depth: depth + 1, pathname, items: childItems }) : null}
      </NavItem>
    );

    return acc;
  }, []);

  return (
    <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

function NavItem({
  children,
  depth,
  disabled,
  external,
  forceOpen = false,
  href,
  icon,
  itemKey,
  label,
  matcher,
  pathname,
  title,
}) {
  const [open, setOpen] = React.useState(forceOpen);
  const { isPinningEnabled, pinnedKeys, togglePin } = React.useContext(PinContext);
  const active = isNavItemActive({ disabled, external, href, matcher, pathname });
  const Icon = icon ? icons[icon] : null;
  const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
  const isBranch = children && !href;
  const showChildren = Boolean(children && open);
  const isPinnable = Boolean(isPinningEnabled && itemKey && href && !isBranch);
  const isPinned = isPinnable && pinnedKeys.includes(itemKey);

  return (
    <Box component="li" data-depth={depth} sx={{ userSelect: 'none' }}>
      <Box
        {...(isBranch
          ? {
              onClick: () => {
                setOpen(!open);
              },
              onKeyUp: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setOpen(!open);
                }
              },
              role: 'button',
            }
          : {
              ...(href
                ? {
                    component: external ? 'a' : RouterLink,
                    href,
                    target: external ? '_blank' : undefined,
                    rel: external ? 'noreferrer' : undefined,
                  }
                : { role: 'button' }),
            })}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'var(--NavItem-color)',
          cursor: 'pointer',
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          p: '6px 16px',
          position: 'relative',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          ...(disabled && {
            bgcolor: 'var(--NavItem-disabled-background)',
            color: 'var(--NavItem-disabled-color)',
            cursor: 'not-allowed',
          }),
          ...(active && {
            bgcolor: 'var(--NavItem-active-background)',
            color: 'var(--NavItem-active-color)',
            ...(depth > 0 && {
              '&::before': {
                bgcolor: 'var(--NavItem-children-indicator)',
                borderRadius: '2px',
                content: '" "',
                height: '20px',
                left: '-14px',
                position: 'absolute',
                width: '3px',
              },
            }),
          }),
          ...(open && { color: 'var(--NavItem-open-color)' }),
          '&:hover': {
            ...(!disabled &&
              !active && { bgcolor: 'var(--NavItem-hover-background)', color: 'var(--NavItem-hover-color)' }),
          },
          '& .nav-pin-toggle': { opacity: 0, transition: 'opacity 120ms' },
          '&:hover .nav-pin-toggle, & .nav-pin-toggle.pinned, &:focus-within .nav-pin-toggle': {
            opacity: 1,
          },
        }}
        tabIndex={0}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
          {Icon ? (
            <Icon
              fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
              fontSize="var(--icon-fontSize-md)"
              weight={forceOpen || active ? 'fill' : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
          >
            {title}
          </Typography>
        </Box>
        {label ? <Chip color="primary" label={label} size="small" /> : null}
        {external ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
        {isPinnable ? (
          <Tooltip title={isPinned ? 'Unpin' : 'Pin to top'} placement="right">
            <Box
              aria-label={isPinned ? 'Unpin from sidebar top' : 'Pin to sidebar top'}
              className={`nav-pin-toggle${isPinned ? ' pinned' : ''}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                togglePin(itemKey);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  togglePin(itemKey);
                }
              }}
              role="button"
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '0 0 auto',
                cursor: 'pointer',
                p: '2px',
                borderRadius: 1,
                '&:hover': { bgcolor: 'var(--NavItem-hover-background)' },
              }}
              tabIndex={0}
            >
              <PushPinIcon
                color="var(--NavItem-icon-color)"
                fontSize="var(--icon-fontSize-sm)"
                weight={isPinned ? 'fill' : 'regular'}
              />
            </Box>
          </Tooltip>
        ) : null}
        {isBranch ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
      </Box>
      {showChildren ? (
        <Box sx={{ pl: '24px' }}>
          <Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
        </Box>
      ) : null}
    </Box>
  );
}

Sidebar.displayName = 'Sidebar';
export default Sidebar;
